'use client';

import React, { useCallback, useState } from 'react';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { IResortTopRated, ResortTopRated } from '@/api/home/types';
import { HomeBestResorts } from '@/components/home/BestResorts/HomeBestResorts';
import { useAnalytics } from '@/tracking/analytics';
import { SanitizeMode, useSanitizedHtmlProps } from '@/util/sanitized';
import { Heading } from '@/components/ui/heading';
import { CategoryChips, type CategoryChipItem } from '@/components/common/CategoryChips';
import {
  bestResortsClass,
  bestResortsDescriptionClass,
  bestResortsTitleClass,
} from './styles';

export const BestResorts = ({
  resortTopRatingOverall,
  regionUUID,
  pageType,
  isUsedInHomePage = false,
  displayMode = 'carousel',
}: {
  resortTopRatingOverall: Array<IResortTopRated>,
  regionUUID?: string,
  regionTitle?: string,
  pageType?: 'global' | undefined,
  isFullWidth?: boolean,
  isUsedInHomePage?: boolean,
  /** Layout override for the Array (HomeBestResorts) branch.
   *  - "carousel" (default): single-row swipe rail / chevron-nav rail.
   *  - "grid": 2 rows of 3 cards (6 total) in a static grid. */
  displayMode?: 'carousel' | 'grid',
}) => {
  const { t } = useOTSTranslation('common');
  const [activeTab, setActiveTab] = useState<ResortTopRated>(ResortTopRated.overall);
  const sanitizedTopRated = useSanitizedHtmlProps(
    t('homePage.topRated' as any),
    SanitizeMode.Strict,
  );
  // Categories mirror the Region Guide chip rail so the two surfaces
  // read as one product. Order is intentional (most-used first).
  // Order mirrors the production category rail.
  const chipItems: ReadonlyArray<CategoryChipItem<ResortTopRated>> = [
    { key: ResortTopRated.overall, label: t('region.resortGuide.overallTitle') },
    { key: ResortTopRated.allMtnTerrain, label: t('region.resortGuide.allMtnTerrainTitle') },
    { key: ResortTopRated.familyFriendly, label: t('region.resortGuide.familyFriendlyTitle') },
    { key: ResortTopRated.terrainPark, label: t('region.resortGuide.terrainParkTitle') },
    { key: ResortTopRated.beginner, label: t('region.resortGuide.beginnerTitle') },
    { key: ResortTopRated.apresSki, label: t('region.resortGuide.apresSkiTitle') },
    { key: ResortTopRated.smallSkiArea, label: t('region.resortGuide.smallSkiAreaTitle') },
    { key: ResortTopRated.intermediate, label: t('region.resortGuide.intermediateTitle') },
    { key: ResortTopRated.expert, label: t('region.resortGuide.expertTitle') },
  ];

  const a = useAnalytics();

  const setActiveCategory = useCallback((cat: ResortTopRated) => {
    const chosen = chipItems.find((c) => c.key === cat);
    if (chosen) a.reviewCategory({ content: chosen.label });
    setActiveTab(cat);
  }, [a, setActiveTab, chipItems]);

  return (
    <section className={bestResortsClass}>
      <Heading level={2} className={bestResortsTitleClass}>
        {t('global.resortGuide.TopRatedResorts')}
      </Heading>
      {isUsedInHomePage && <p className={bestResortsDescriptionClass} {...sanitizedTopRated} />}
      <HomeBestResorts
        resortTopRatingOverall={resortTopRatingOverall}
        activeTab={activeTab}
        regionUUID={regionUUID}
        pageType={pageType}
        isUsedInHomePage={isUsedInHomePage}
        displayMode={displayMode}
        headerLeft={(
          <CategoryChips
            items={chipItems}
            active={activeTab}
            onSelect={setActiveCategory}
            ariaLabel={t('common.topRatedCategories')}
          />
        )}
      />
    </section>
  );
};
